// Friends / social graph helpers. RLS enforces who can do what; these
// functions are thin wrappers that return shape-stable summaries for the UI.
//
// Inputs that flow to PostgREST `.or(...)` filters (which are string-parsed
// server-side) are validated as UUIDs before interpolation. PostgREST's
// .or() does not parameterize values, so any unvalidated string here would
// be a parser-injection vector even though it cannot reach raw SQL.

import { getSupabase } from "./client";
import { getUserId } from "./auth";

export type FriendStatus = "pending_in" | "pending_out" | "accepted" | "blocked";

export interface FriendSummary {
  userId:      string;
  displayName: string;
  friendCode:  string;
  status:      FriendStatus;
  since:       string;
  avatarId:    string | null;
}

export interface PlayerSearchResult {
  userId:      string;
  displayName: string;
  friendCode:  string;
  avatarId:    string | null;
}

export type FriendsResult = { ok: true } | { ok: false; error: string };

const FRIEND_CODE_RE = /^[A-Z]{2,8}(-[A-Z]{2,8}){3}$/;
const UUID_RE        = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// PostgREST .ilike() treats % and _ as wildcards. Escape user input so
// "100%" searches for the literal string instead of matching everything.
function escapeIlike(s: string): string {
  return s.replace(/[\\%_]/g, m => "\\" + m);
}

function fail(msg: string): FriendsResult { return { ok: false, error: msg }; }
const OK: FriendsResult = { ok: true };

/** Shape returned by Supabase joins on profiles. */
interface ProfileRef {
  display_name: string | null;
  friend_code:  string | null;
  avatar_id:    string | null;
}
interface FriendshipRow {
  requester_id: string;
  addressee_id: string;
  status:       "pending" | "accepted" | "blocked";
  created_at:   string;
  requester?:   ProfileRef | ProfileRef[] | null;
  addressee?:   ProfileRef | ProfileRef[] | null;
}

/** Supabase returns embedded refs as either an object or array; normalize. */
function unwrapRef(ref: ProfileRef | ProfileRef[] | null | undefined): ProfileRef | null {
  if (!ref) return null;
  return Array.isArray(ref) ? (ref[0] ?? null) : ref;
}

/* -------------------------------------------------------------------------- */
/*                                  Reads                                     */
/* -------------------------------------------------------------------------- */

export async function fetchFriends(): Promise<FriendSummary[]> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return [];
  const { data, error } = await sb
    .from("my_friends")
    .select("friend_id, display_name, friend_code, friends_since, avatar_id")
    .order("display_name", { ascending: true });
  if (error) { console.warn("[friends] list failed", error); return []; }
  return (data ?? []).map((r): FriendSummary => ({
    userId:      r.friend_id,
    displayName: r.display_name,
    friendCode:  r.friend_code,
    status:      "accepted",
    since:       r.friends_since,
    avatarId:    r.avatar_id ?? null
  }));
}

export async function fetchPendingRequests(): Promise<FriendSummary[]> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return [];
  const { data, error } = await sb
    .from("friendships")
    .select("requester_id, addressee_id, status, created_at, " +
            "requester:profiles!friendships_requester_id_fkey(display_name, friend_code, avatar_id), " +
            "addressee:profiles!friendships_addressee_id_fkey(display_name, friend_code, avatar_id)")
    .eq("status", "pending");
  if (error) { console.warn("[friends] pending failed", error); return []; }
  return ((data ?? []) as unknown as FriendshipRow[]).map((r): FriendSummary => {
    const incoming = r.addressee_id === uid;
    const other = unwrapRef(incoming ? r.requester : r.addressee);
    return {
      userId:      incoming ? r.requester_id : r.addressee_id,
      displayName: other?.display_name ?? "Player",
      friendCode:  other?.friend_code  ?? "",
      status:      incoming ? "pending_in" : "pending_out",
      since:       r.created_at,
      avatarId:    other?.avatar_id ?? null
    };
  });
}

export async function fetchBlocked(): Promise<FriendSummary[]> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return [];
  const { data, error } = await sb
    .from("friendships")
    .select("requester_id, addressee_id, created_at, " +
            "requester:profiles!friendships_requester_id_fkey(display_name, friend_code, avatar_id), " +
            "addressee:profiles!friendships_addressee_id_fkey(display_name, friend_code, avatar_id)")
    .eq("status", "blocked");
  if (error) { console.warn("[friends] blocked failed", error); return []; }
  return ((data ?? []) as unknown as FriendshipRow[]).map((r): FriendSummary => {
    const otherIsAddressee = r.requester_id === uid;
    const other = unwrapRef(otherIsAddressee ? r.addressee : r.requester);
    return {
      userId:      otherIsAddressee ? r.addressee_id : r.requester_id,
      displayName: other?.display_name ?? "Player",
      friendCode:  other?.friend_code  ?? "",
      status:      "blocked",
      since:       r.created_at,
      avatarId:    other?.avatar_id ?? null
    };
  });
}

export async function searchPlayers(q: string): Promise<PlayerSearchResult[]> {
  const sb = getSupabase();
  if (!sb) return [];
  const term = q.trim();
  if (term.length < 2) return [];
  const { data, error } = await sb
    .from("profiles")
    .select("user_id, display_name, friend_code, avatar_id")
    .ilike("display_name", `%${escapeIlike(term)}%`)
    .limit(20);
  if (error) { console.warn("[friends] search failed", error); return []; }
  return (data ?? []).map((r): PlayerSearchResult => ({
    userId:      r.user_id,
    displayName: r.display_name,
    friendCode:  r.friend_code,
    avatarId:    r.avatar_id ?? null
  }));
}

export async function lookupByFriendCode(code: string): Promise<PlayerSearchResult | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const norm = code.trim().toUpperCase();
  if (!FRIEND_CODE_RE.test(norm)) return null;
  const { data, error } = await sb
    .from("profiles")
    .select("user_id, display_name, friend_code, avatar_id")
    .eq("friend_code", norm)
    .maybeSingle();
  if (error) { console.warn("[friends] lookup failed", error); return null; }
  if (!data) return null;
  return { userId: data.user_id, displayName: data.display_name, friendCode: data.friend_code, avatarId: data.avatar_id ?? null };
}

/* -------------------------------------------------------------------------- */
/*                                  Writes                                    */
/* -------------------------------------------------------------------------- */

/** Build the canonical "row involving uid and other" filter for .or() — both
 *  sides validated as UUIDs before interpolation. */
function pairFilter(uid: string, other: string): string {
  return `and(requester_id.eq.${uid},addressee_id.eq.${other}),and(requester_id.eq.${other},addressee_id.eq.${uid})`;
}

export async function sendFriendRequest(targetUserId: string): Promise<FriendsResult> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return fail("not signed in");
  if (!UUID_RE.test(targetUserId)) return fail("invalid id");
  if (targetUserId === uid) return fail("you can't friend yourself");

  // Edge case: addressee already sent us a request → just accept it.
  const { data: existing } = await sb
    .from("friendships")
    .select("requester_id, addressee_id, status")
    .or(pairFilter(uid, targetUserId))
    .maybeSingle();
  if (existing) {
    if (existing.status === "accepted") return fail("already friends");
    if (existing.status === "blocked")  return fail("blocked");
    if (existing.status === "pending" && existing.addressee_id === uid) {
      return respondToRequest(existing.requester_id, true);
    }
    return fail("request already pending");
  }

  const { error } = await sb.from("friendships").insert({
    requester_id: uid,
    addressee_id: targetUserId,
    status: "pending"
  });
  return error ? fail(error.message) : OK;
}

export async function sendFriendRequestByCode(code: string): Promise<FriendsResult> {
  const target = await lookupByFriendCode(code);
  if (!target) return fail("no player with that code");
  return sendFriendRequest(target.userId);
}

export async function respondToRequest(
  requesterId: string, accept: boolean
): Promise<FriendsResult> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return fail("not signed in");
  if (!UUID_RE.test(requesterId)) return fail("invalid id");

  if (accept) {
    const { error } = await sb.from("friendships")
      .update({ status: "accepted", responded_at: new Date().toISOString() })
      .eq("requester_id", requesterId)
      .eq("addressee_id", uid);
    return error ? fail(error.message) : OK;
  }
  const { error } = await sb.from("friendships")
    .delete()
    .eq("requester_id", requesterId)
    .eq("addressee_id", uid);
  return error ? fail(error.message) : OK;
}

export async function cancelOutgoingRequest(addresseeId: string): Promise<FriendsResult> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return fail("not signed in");
  if (!UUID_RE.test(addresseeId)) return fail("invalid id");
  const { error } = await sb.from("friendships")
    .delete()
    .eq("requester_id", uid)
    .eq("addressee_id", addresseeId);
  return error ? fail(error.message) : OK;
}

export async function removeFriend(otherUserId: string): Promise<FriendsResult> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return fail("not signed in");
  if (!UUID_RE.test(otherUserId)) return fail("invalid id");
  const { error } = await sb.from("friendships")
    .delete()
    .or(pairFilter(uid, otherUserId));
  return error ? fail(error.message) : OK;
}

/**
 * Block a user. Delegates to a SECURITY DEFINER Postgres RPC so the
 * "select-then-insert-then-update" sequence runs atomically server-side
 * without TOCTOU races between concurrent requests.
 */
export async function blockUser(targetUserId: string): Promise<FriendsResult> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return fail("not signed in");
  if (!UUID_RE.test(targetUserId)) return fail("invalid id");
  if (targetUserId === uid) return fail("you can't block yourself");

  const { error } = await sb.rpc("block_user", { p_target: targetUserId });
  return error ? fail(error.message) : OK;
}

export async function unblockUser(otherUserId: string): Promise<FriendsResult> {
  return removeFriend(otherUserId);
}
